import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { X } from 'lucide-react';
import { useAddEmployeeMutation, useUpdateEmployeeMutation } from '../../store/slices/employeeSlice';
import { useGetDepartmentsQuery } from '../../store/slices/departmentSlice';
import { useGetProfilesQuery } from '../../store/slices/adminSlice';
import { StatusPill } from '../ui';
import type { StatusTone } from '../ui';
import { sbg, typeStyle } from '../../theme/sbgTokens';
import { formatNumber } from '../../utils/format';
import { ACCESS_TAB_LOCATION } from '../../utils/settingsTabs';

interface EmployeeFormModalProps {
  onClose: () => void;
  employee?: any;
  isEdit?: boolean;
}

type EmploymentStatus = 'active' | 'terminated' | 'resigned_regrettable' | 'resigned_unregrettable';

/** Only Active is a state worth color; former statuses are neutral facts. */
const STATUS_OPTIONS: Record<EmploymentStatus, { label: string; tone: StatusTone }> = {
  active: { label: 'Active', tone: 'success' },
  terminated: { label: 'Terminated', tone: 'neutral' },
  resigned_regrettable: { label: 'Resigned (regrettable)', tone: 'neutral' },
  resigned_unregrettable: { label: 'Resigned (unregrettable)', tone: 'neutral' },
};

interface EmployeeFormState {
  name: string;
  email: string;
  role: string;
  departmentId: string;
  teamName: string;
  managerName: string;
  managerUserId: string;
  jrAeUserId: string;
  userId: string;
  vpName: string;
  startDate: string;
  customAllotment: boolean;
  ptoDays: string;
  sickDays: string;
  wfhEligible: boolean;
  employmentStatus: EmploymentStatus;
  terminationDate: string;
}

type FieldErrors = Partial<Record<keyof EmployeeFormState, string>>;

const HOURS_PER_DAY = 8;

/** Fields that can carry an error, in the order they appear on screen. */
const ERROR_ORDER: (keyof EmployeeFormState)[] = ['name', 'email', 'startDate', 'role', 'departmentId', 'jrAeUserId', 'ptoDays', 'sickDays'];

/** Field names as the error summary lists them (the same words as each label). */
const FIELD_LABELS: Partial<Record<keyof EmployeeFormState, string>> = {
  name: 'Full name',
  email: 'Work email',
  startDate: 'Start date',
  role: 'Role',
  departmentId: 'Department',
  jrAeUserId: 'Jr manager',
  ptoDays: 'PTO',
  sickDays: 'Sick',
};

/** A light shape check (something@domain.tld); the server stays the source of truth. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** "120 h" for a days value, or null while the field isn't a number yet. */
const hoursFor = (days: string) => {
  const raw = String(days).trim();
  const value = Number(raw);
  if (!raw || Number.isNaN(value) || value < 0) return null;
  const hours = value * HOURS_PER_DAY;
  return `${formatNumber(hours, Number.isInteger(hours) ? 0 : 1)} h`;
};

const fieldId = (name: keyof EmployeeFormState) => `employee-form-${name}`;

/* ------------------------------------------------------------------------ */
/* Layout helpers                                                            */
/* ------------------------------------------------------------------------ */

/** "Team (optional)": the exception is marked, never a forest of asterisks. */
const optionalLabel = (label: string) => (
  <>
    {label}{' '}
    <Box component="span" sx={{ ...typeStyle('body-sm'), color: sbg.text.secondary }}>
      (optional)
    </Box>
  </>
);

const placeholderText = (text: string) => (
  <Box component="span" sx={{ color: sbg.text.tertiary }}>
    {text}
  </Box>
);

const FormSection: React.FC<{ id: string; title: string; description?: string; first?: boolean; children: React.ReactNode }> = ({
  id,
  title,
  description,
  first = false,
  children,
}) => (
  <Box
    component="section"
    aria-labelledby={id}
    sx={{
      display: 'grid',
      gap: 2,
      ...(first ? null : { pt: 3, borderTop: `1px solid ${sbg.border.subtle}` }),
    }}
  >
    <Box>
      <Typography id={id} variant="h4" component="h3" sx={{ color: sbg.text.primary }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, mt: 0.25 }}>
          {description}
        </Typography>
      )}
    </Box>
    {children}
  </Box>
);

const FieldGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
      columnGap: 2,
      rowGap: 2.5,
      alignItems: 'start',
    }}
  >
    {children}
  </Box>
);

/** A checkbox with a title and one line of explanation: a plain row, no box around it. */
const CheckboxRow: React.FC<{
  name: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  title: string;
  description: string;
}> = ({ name, checked, onChange, title, description }) => {
  const descriptionId = `${fieldId(name as keyof EmployeeFormState)}-description`;
  return (
    <FormControlLabel
      sx={{ gridColumn: '1 / -1', m: 0, alignItems: 'flex-start', gap: 1.25 }}
      control={
        <Checkbox
          name={name}
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          inputProps={{ 'aria-describedby': descriptionId }}
          sx={{ mt: '-2px' }}
        />
      }
      label={
        <Box>
          <Typography variant="labelMd" component="span" sx={{ display: 'block', color: sbg.text.primary }}>
            {title}
          </Typography>
          <Typography id={descriptionId} variant="body2" component="span" sx={{ display: 'block', color: sbg.text.secondary, mt: 0.25 }}>
            {description}
          </Typography>
        </Box>
      }
    />
  );
};

/* ------------------------------------------------------------------------ */
/* Form                                                                      */
/* ------------------------------------------------------------------------ */

const EmployeeFormModal: React.FC<EmployeeFormModalProps> = ({ onClose, employee, isEdit = false }) => {
  const { data: departments = [] } = useGetDepartmentsQuery();
  const { data: profiles = [] } = useGetProfilesQuery();
  const [addEmployee, { isLoading: isAdding }] = useAddEmployeeMutation();
  const [updateEmployee, { isLoading: isUpdating }] = useUpdateEmployeeMutation();
  const [errors, setErrors] = useState<FieldErrors>({});
  // Blur only checks fields the person has changed (or everything after a
  // submit attempt), so leaving an untouched field never flags it.
  const [touched, setTouched] = useState<ReadonlySet<keyof EmployeeFormState>>(() => new Set());
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const fieldRefs = useRef<Partial<Record<keyof EmployeeFormState, { focus: () => void } | null>>>({});
  const submitErrorRef = useRef<HTMLDivElement | null>(null);
  const [formData, setFormData] = useState<EmployeeFormState>({
    name: '',
    email: '',
    role: '',
    departmentId: '',
    teamName: '',
    managerName: '',
    managerUserId: '',
    jrAeUserId: '',
    userId: '',
    vpName: '',
    startDate: '',
    customAllotment: false,
    ptoDays: '0',
    sickDays: '0',
    wfhEligible: false,
    employmentStatus: 'active' as EmploymentStatus,
    terminationDate: '',
  });

  const isSubmitting = isAdding || isUpdating;

  useEffect(() => {
    if (employee && isEdit) {
      setFormData({
        name: employee.name || '',
        email: employee.email || '',
        role: employee.role || '',
        departmentId: employee.department?._id || '',
        teamName: employee.teamName || '',
        managerName: employee.managerName || '',
        managerUserId: employee.managerUserId || '',
        jrAeUserId: employee.jrAeUserId || '',
        userId: employee.userId || '',
        vpName: employee.vpName || '',
        startDate: employee.startDate || '',
        customAllotment: employee.customAllotment ?? false,
        ptoDays: String((employee.ptoAllotment || 0) / HOURS_PER_DAY),
        sickDays: String((employee.sickTimeAllotment || 0) / HOURS_PER_DAY),
        wfhEligible: employee.wfhEligible ?? (employee.wfhAllotment > 0),
        employmentStatus: employee.employmentStatus || 'active',
        terminationDate: employee.terminationDate || '',
      });
    }
  }, [employee, isEdit]);

  useEffect(() => {
    if (submitError) submitErrorRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [submitError]);

  const departmentOptions = useMemo(() => departments, [departments]);
  const managerProfiles = useMemo(
    () => profiles.filter((profile) =>
      profile.role === 'admin' || profile.role === 'hr' || profile.role === 'manager'
    ),
    [profiles]
  );
  const jrAeProfiles = useMemo(
    () => profiles
      .filter((profile) => profile.role === 'jr_ae')
      .sort((left, right) =>
        (left.fullName || left.email).localeCompare(right.fullName || right.email)
      ),
    [profiles]
  );
  const vpNames = useMemo(
    () => Array.from(new Set(
      profiles
        .filter((p) => p.fullName)
        .map((p) => p.fullName as string)
        .sort()
    )),
    [profiles]
  );

  const profileLabel = (id: string) => {
    const profile = profiles.find((item) => item.id === id);
    return profile ? profile.fullName || profile.email : 'Linked account';
  };

  // Start date drives the tenure allotment policy; it is required for new
  // policy-managed hires. Negotiated exceptions (custom allotment) may omit
  // it — the policy never touches them. When editing, it mirrors the backend
  // guard: clearing it while policy-managed would strand the employee.
  const isStartDateRequired = (data: EmployeeFormState) => {
    if (data.customAllotment) return false;
    if (!isEdit) return true;
    return (employee?.customAllotment ?? false) || !!employee?.startDate;
  };

  const computeErrors = (data: EmployeeFormState): FieldErrors => {
    const nextErrors: FieldErrors = {};
    if (!data.name.trim()) nextErrors.name = 'Enter the employee’s full name.';
    if (!data.email.trim()) nextErrors.email = 'Enter a work email.';
    else if (!EMAIL_PATTERN.test(data.email.trim())) nextErrors.email = 'Enter an email like maria.chen@sbgfunding.com.';
    if (!data.role.trim()) nextErrors.role = 'Enter a role, such as Account Executive.';
    if (!data.departmentId) nextErrors.departmentId = 'Choose a department.';
    if (
      data.jrAeUserId &&
      data.userId &&
      data.jrAeUserId === data.userId
    ) {
      nextErrors.jrAeUserId = 'An employee can’t be their own Jr manager. Choose someone else or None.';
    }
    if (!isEdit && !data.customAllotment && !data.startDate) {
      nextErrors.startDate = 'Enter a start date. It sets their tenure-based PTO.';
    }
    if (isEdit && !data.customAllotment && !data.startDate) {
      const hadCustom = employee?.customAllotment ?? false;
      const hadStartDate = !!employee?.startDate;
      if (hadCustom || hadStartDate) {
        nextErrors.startDate = 'Enter a start date. The tenure policy needs it to manage allotments.';
      }
    }

    if (data.customAllotment) {
      // Custom hours must be stated explicitly. Check the raw string first:
      // Number('') is 0, so a cleared field would otherwise silently save a
      // negotiated exception with 0 hours the tenure job never fills.
      const fields = ['ptoDays', 'sickDays'] as const;
      fields.forEach((field) => {
        const raw = String(data[field]).trim();
        const value = Number(raw);
        if (!raw || Number.isNaN(value) || value < 0) {
          nextErrors[field] = 'Enter the negotiated days (0 or more).';
        }
      });
    }

    return nextErrors;
  };

  /** Re-check fields that already show an error, so a fix clears it at once. */
  const revalidate = (data: EmployeeFormState, fields: (keyof EmployeeFormState)[]) => {
    if (!fields.some((field) => errors[field])) return;
    const next = computeErrors(data);
    setErrors((current) => {
      const updated = { ...current };
      fields.forEach((field) => {
        if (!current[field]) return;
        if (next[field]) updated[field] = next[field];
        else delete updated[field];
      });
      return updated;
    });
  };

  /** Validate one field when the person leaves it — once they've changed it, or after a submit attempt. */
  const handleBlur = (field: keyof EmployeeFormState) => () => {
    if (!submitAttempted && !touched.has(field)) return;
    const message = computeErrors(formData)[field];
    setErrors((current) => {
      if (message === current[field]) return current;
      const updated = { ...current };
      if (message) updated[field] = message;
      else delete updated[field];
      return updated;
    });
  };

  const updateFields = (patch: Partial<EmployeeFormState>, dependents: (keyof EmployeeFormState)[] = []) => {
    const next = { ...formData, ...patch };
    const changed = Object.keys(patch) as (keyof EmployeeFormState)[];
    setFormData(next);
    setTouched((current) => (changed.every((field) => current.has(field)) ? current : new Set([...Array.from(current), ...changed])));
    revalidate(next, [...changed, ...dependents]);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    const field = name as keyof EmployeeFormState;
    updateFields({ [field]: value } as Partial<EmployeeFormState>, field === 'userId' ? ['jrAeUserId'] : []);
  };

  const handleManagerUserChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const managerUserId = event.target.value;
    const matchedProfile = profiles.find((profile) => profile.id === managerUserId);
    updateFields({
      managerUserId,
      managerName: matchedProfile?.fullName || formData.managerName,
    });
  };

  const register = (field: keyof EmployeeFormState) => (element: { focus: () => void } | null) => {
    fieldRefs.current[field] = element;
  };

  /** Props shared by every validated field: id, error state, message, blur check, focus ref. */
  const fieldProps = (field: keyof EmployeeFormState, helperText?: React.ReactNode) => ({
    id: fieldId(field),
    name: field,
    error: Boolean(errors[field]),
    helperText: errors[field] || helperText,
    onBlur: handleBlur(field),
    inputRef: register(field),
    fullWidth: true,
  });

  const handleSubmit = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (isSubmitting) return;
    setSubmitError(null);
    setSubmitAttempted(true);

    const nextErrors = computeErrors(formData);
    setErrors(nextErrors);
    const firstError = ERROR_ORDER.find((field) => nextErrors[field]);
    if (firstError) {
      fieldRefs.current[firstError]?.focus();
      return;
    }

    const payload = {
      name: formData.name.trim(),
      email: formData.email.trim().toLowerCase(),
      role: formData.role.trim(),
      department: formData.departmentId,
      teamName: formData.teamName.trim() || null,
      managerName: formData.managerName.trim() || null,
      managerUserId: formData.managerUserId || null,
      jrAeUserId: formData.jrAeUserId || null,
      userId: formData.userId || null,
      vpName: formData.vpName.trim() || null,
      startDate: formData.startDate || null,
      customAllotment: formData.customAllotment,
      // Manual allotments only apply to negotiated exceptions; otherwise the
      // backend computes them from the tenure policy.
      ...(formData.customAllotment
        ? {
            ptoAllotment: Number(formData.ptoDays) * HOURS_PER_DAY,
            sickTimeAllotment: Number(formData.sickDays) * HOURS_PER_DAY,
          }
        : {}),
      wfhEligible: formData.wfhEligible,
      wfhAllotment: formData.wfhEligible ? 2 : 0, // 2 days/week for hybrid
      employmentStatus: formData.employmentStatus,
      terminationDate: formData.terminationDate || null,
    } as any;

    try {
      if (isEdit && employee?._id) {
        await updateEmployee({ id: employee._id, employee: payload }).unwrap();
      } else {
        await addEmployee(payload).unwrap();
      }
      onClose();
    } catch (err: any) {
      const reason = String(err?.data?.error || err?.data?.message || err?.error || '').trim().replace(/\.$/, '');
      setSubmitError(
        `Couldn’t save ${isEdit ? 'your changes' : 'the new employee'}. ${reason || 'The server didn’t respond'}. ` +
        'Your entries are still here; try again in a minute.'
      );
    }
  };

  const startDateRequired = isStartDateRequired(formData);
  const errorFields = submitAttempted ? ERROR_ORDER.filter((field) => errors[field]) : [];
  const ptoHours = hoursFor(formData.ptoDays);
  const sickHours = hoursFor(formData.sickDays);
  const ptoHelper = formData.customAllotment
    ? `Days per year${ptoHours ? ` · ${ptoHours}` : ''}.`
    : isEdit && ptoHours
      ? `Set by the tenure policy · ${ptoHours} a year.`
      : 'Set by the tenure policy.';
  const sickHelper = formData.customAllotment
    ? `Days per year${sickHours ? ` · ${sickHours}` : ''}.`
    : 'Fixed at 7 days (56 h) of sick and safe leave.';
  const showPolicyPlaceholder = !formData.customAllotment && !isEdit;
  const title = isEdit ? (employee?.name ? `Edit ${employee.name}` : 'Edit employee') : 'Add employee';

  return (
    <>
      <DialogTitle sx={{ pr: 8 }}>{title}</DialogTitle>
      <Typography variant="body1" component="p" sx={{ color: sbg.text.secondary, px: 3, pb: 2, pr: 8 }}>
        {isEdit
          ? 'Changes apply to time off, approvals and reports as soon as you save.'
          : 'PTO follows the tenure policy from their start date unless you set a custom allotment.'}
      </Typography>
      <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
        <Tooltip title="Close">
          <span>
            <IconButton aria-label="Close" onClick={onClose} disabled={isSubmitting}>
              <X />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Box
        component="form"
        noValidate
        onSubmit={handleSubmit}
        sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: '1 1 auto' }}
      >
        <DialogContent dividers sx={{ display: 'grid', gap: 3, pt: 2.5, pb: 3 }}>
          {errorFields.length > 0 && (
            <Alert severity="error">
              Fix {errorFields.length === 1 ? '1 field' : `${errorFields.length} fields`} to{' '}
              {isEdit ? 'save your changes' : 'add this employee'}:{' '}
              {errorFields.map((field, index) => (
                <React.Fragment key={field}>
                  {index > 0 && ', '}
                  <Link
                    component="button"
                    type="button"
                    onClick={() => fieldRefs.current[field]?.focus()}
                    sx={{ font: 'inherit', verticalAlign: 'baseline', color: 'inherit', textDecoration: 'underline' }}
                  >
                    {FIELD_LABELS[field]}
                  </Link>
                </React.Fragment>
              ))}
              .
            </Alert>
          )}

          {submitError && (
            <Alert severity="error" ref={submitErrorRef}>
              {submitError}
            </Alert>
          )}

          <FormSection id="employee-form-profile" title="Profile" description="Who they are and when they started." first>
            <FieldGrid>
              <TextField
                {...fieldProps('name')}
                label="Full name"
                placeholder="e.g. Maria Chen"
                autoComplete="off"
                autoFocus={!isEdit}
                value={formData.name}
                onChange={handleChange}
                slotProps={{ htmlInput: { 'aria-required': true } }}
              />
              <TextField
                {...fieldProps('email')}
                label="Work email"
                type="email"
                placeholder="e.g. maria.chen@sbgfunding.com"
                autoComplete="off"
                value={formData.email}
                onChange={handleChange}
                slotProps={{ htmlInput: { 'aria-required': true, inputMode: 'email' } }}
              />
              <TextField
                {...fieldProps('startDate', 'Sets tenure-based PTO and first-year proration.')}
                label={startDateRequired ? 'Start date' : optionalLabel('Start date')}
                type="date"
                value={formData.startDate}
                onChange={handleChange}
                slotProps={{ htmlInput: { 'aria-required': startDateRequired, sx: { fontVariantNumeric: 'tabular-nums' } } }}
              />
              <TextField
                select
                {...fieldProps('userId', 'Links this record to their Attendance sign-in.')}
                label={optionalLabel('Employee account')}
                value={formData.userId}
                onChange={handleChange}
                slotProps={{
                  select: {
                    displayEmpty: true,
                    renderValue: (value) => (value ? profileLabel(value as string) : placeholderText('Not linked')),
                  },
                }}
              >
                <MenuItem value="">Not linked</MenuItem>
                {profiles.map((profile) => (
                  <MenuItem key={profile.id} value={profile.id}>
                    {profile.fullName || profile.email}
                  </MenuItem>
                ))}
              </TextField>
            </FieldGrid>
          </FormSection>

          <FormSection id="employee-form-role" title="Role and team" description="Where they sit and who approves their requests.">
            <FieldGrid>
              <TextField
                {...fieldProps('role')}
                label="Role"
                placeholder="e.g. Account Executive"
                autoComplete="off"
                value={formData.role}
                onChange={handleChange}
                slotProps={{ htmlInput: { 'aria-required': true } }}
              />
              <TextField
                select
                {...fieldProps('departmentId')}
                label="Department"
                value={formData.departmentId}
                onChange={handleChange}
                slotProps={{
                  select: {
                    displayEmpty: true,
                    SelectDisplayProps: { 'aria-required': true } as React.HTMLAttributes<HTMLDivElement>,
                    renderValue: (value) =>
                      value
                        ? departmentOptions.find((dept) => dept._id === value)?.name ?? 'Unknown department'
                        : placeholderText('Choose a department'),
                  },
                }}
              >
                <MenuItem value="">Choose a department</MenuItem>
                {departmentOptions.map((dept) => (
                  <MenuItem key={dept._id} value={dept._id}>
                    {dept.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                id={fieldId('teamName')}
                name="teamName"
                label={optionalLabel('Team')}
                placeholder="e.g. Team 3"
                autoComplete="off"
                value={formData.teamName}
                onChange={handleChange}
                fullWidth
              />
              <TextField
                select
                {...fieldProps('jrAeUserId', `Only people with the Jr manager role appear. Assign it in ${ACCESS_TAB_LOCATION}.`)}
                label={optionalLabel('Jr manager')}
                value={formData.jrAeUserId}
                onChange={handleChange}
                slotProps={{
                  select: {
                    displayEmpty: true,
                    renderValue: (value) => (value ? profileLabel(value as string) : placeholderText('None')),
                  },
                }}
              >
                <MenuItem value="">None</MenuItem>
                {jrAeProfiles.map((profile) => (
                  <MenuItem
                    key={profile.id}
                    value={profile.id}
                    disabled={profile.id === formData.userId}
                  >
                    {profile.fullName || profile.email}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                id={fieldId('managerUserId')}
                name="managerUserId"
                label={optionalLabel('Manager account')}
                helperText="Choosing an account fills in the manager name."
                value={formData.managerUserId}
                onChange={handleManagerUserChange}
                fullWidth
                slotProps={{
                  select: {
                    displayEmpty: true,
                    renderValue: (value) => (value ? profileLabel(value as string) : placeholderText('Not linked')),
                  },
                }}
              >
                <MenuItem value="">Not linked</MenuItem>
                {managerProfiles.map((profile) => (
                  <MenuItem key={profile.id} value={profile.id}>
                    {profile.fullName || profile.email}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                id={fieldId('managerName')}
                name="managerName"
                label={optionalLabel('Manager name')}
                placeholder="e.g. Brett Green"
                autoComplete="off"
                value={formData.managerName}
                onChange={handleChange}
                fullWidth
              />
              <TextField
                select
                id={fieldId('vpName')}
                name="vpName"
                label={optionalLabel('VP for WFH approvals')}
                helperText="Approves their WFH override requests."
                value={formData.vpName}
                onChange={handleChange}
                fullWidth
                slotProps={{
                  select: {
                    displayEmpty: true,
                    renderValue: (value) => (value ? String(value) : placeholderText('None')),
                  },
                }}
              >
                <MenuItem value="">None</MenuItem>
                {vpNames.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
            </FieldGrid>
          </FormSection>

          <FormSection id="employee-form-time-off" title="Time off" description="Yearly allotments and their work-from-home schedule.">
            <FieldGrid>
              <CheckboxRow
                name="customAllotment"
                checked={formData.customAllotment}
                onChange={(checked) => updateFields({ customAllotment: checked }, ['startDate', 'ptoDays', 'sickDays'])}
                title="Custom allotment"
                description="A negotiated exception. The tenure policy won’t change the days you set here."
              />
              <TextField
                {...fieldProps('ptoDays', ptoHelper)}
                label="PTO"
                value={showPolicyPlaceholder ? '' : formData.ptoDays}
                placeholder={showPolicyPlaceholder ? 'Set by policy' : undefined}
                onChange={handleChange}
                disabled={!formData.customAllotment}
                slotProps={{
                  htmlInput: { inputMode: 'decimal', sx: { fontVariantNumeric: 'tabular-nums' } },
                  input: { endAdornment: <InputAdornment position="end">days</InputAdornment> },
                }}
              />
              <TextField
                {...fieldProps('sickDays', sickHelper)}
                label="Sick"
                value={showPolicyPlaceholder ? '' : formData.sickDays}
                placeholder={showPolicyPlaceholder ? 'Set by policy' : undefined}
                onChange={handleChange}
                disabled={!formData.customAllotment}
                slotProps={{
                  htmlInput: { inputMode: 'decimal', sx: { fontVariantNumeric: 'tabular-nums' } },
                  input: { endAdornment: <InputAdornment position="end">days</InputAdornment> },
                }}
              />
              <CheckboxRow
                name="wfhEligible"
                checked={formData.wfhEligible}
                onChange={(checked) => updateFields({ wfhEligible: checked })}
                title="Hybrid schedule (WFH eligible)"
                description={
                  formData.wfhEligible
                    ? 'Up to 2 WFH days a week. Calendar entries are approved automatically.'
                    : 'Not on a hybrid schedule. WFH requests need VP approval.'
                }
              />
            </FieldGrid>
          </FormSection>

          {isEdit && (
            <FormSection id="employee-form-employment" title="Employment" description="Former employees move to the Former view of the directory.">
              <FieldGrid>
                <TextField
                  select
                  id={fieldId('employmentStatus')}
                  name="employmentStatus"
                  label="Status"
                  value={formData.employmentStatus}
                  onChange={handleChange}
                  fullWidth
                  slotProps={{
                    select: {
                      renderValue: (value) => {
                        const option = STATUS_OPTIONS[value as EmploymentStatus];
                        return option ? <StatusPill tone={option.tone}>{option.label}</StatusPill> : String(value);
                      },
                    },
                  }}
                >
                  {(Object.entries(STATUS_OPTIONS) as [EmploymentStatus, { label: string; tone: StatusTone }][]).map(([value, option]) => (
                    <MenuItem key={value} value={value}>
                      <StatusPill tone={option.tone}>{option.label}</StatusPill>
                    </MenuItem>
                  ))}
                </TextField>
                {formData.employmentStatus !== 'active' && (
                  <TextField
                    id={fieldId('terminationDate')}
                    name="terminationDate"
                    label={optionalLabel('Last day')}
                    helperText="Their final working day."
                    type="date"
                    value={formData.terminationDate}
                    onChange={handleChange}
                    fullWidth
                    slotProps={{ htmlInput: { sx: { fontVariantNumeric: 'tabular-nums' } } }}
                  />
                )}
              </FieldGrid>
            </FormSection>
          )}
        </DialogContent>

        <DialogActions sx={{ pt: 2 }}>
          <Button variant="text" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            aria-busy={isSubmitting || undefined}
            startIcon={isSubmitting ? <CircularProgress size={16} thickness={5} color="inherit" aria-hidden="true" /> : undefined}
          >
            {isSubmitting ? 'Saving…' : isEdit ? 'Save changes' : 'Add employee'}
          </Button>
        </DialogActions>
      </Box>
    </>
  );
};

export default EmployeeFormModal;
